// ============================================================
// ByOurConsent.com -- Homepage
// Matches approved mockup v6
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useWindowWidth from '../hooks/useWindowWidth';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';

const ShieldLarge = () => (
    <svg width="40" height="48" viewBox="0 0 44 52" fill="none">
        <path d="M22 2L3 10v16c0 13 8.5 24.5 19 27.5C33.5 50.5 41 39 41 26V10L22 2z"
              fill="#1e2a3a" opacity="0.85"/>
        <path d="M22 2L3 10v16c0 13 8.5 24.5 19 27.5C33.5 50.5 41 39 41 26V10L22 2z"
              fill="none" stroke="#a09070" strokeWidth="1"/>
        <circle cx="22" cy="28" r="5" fill="none" stroke="#c8beaa" strokeWidth="1.5"/>
        <line x1="22" y1="18" x2="22" y2="23" stroke="#c8beaa" strokeWidth="1.5"/>
    </svg>
);

const PrincipleCard = ({ icon, title, body }) => (
    <div style={{
        background: '#ede8dc', border: '0.5px solid #c8beaa',
        borderRadius: '4px', padding: '0.8rem'
    }}>
        <div style={{ marginBottom: '6px' }}>{icon}</div>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', marginBottom: '3px' }}>
            {title}
        </div>
        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.55 }}>
            {body}
        </div>
    </div>
);

const HubCard = ({ tag, tagColor, title, desc, cta, to }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <Link to={to} style={{ textDecoration: 'none' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background: '#f5f0e8',
                    border: `0.5px solid ${hovered ? '#8b1a1a' : '#c8beaa'}`,
                    borderRadius: '4px', padding: '0.85rem',
                    cursor: 'pointer', display: 'flex',
                    flexDirection: 'column', gap: '5px',
                    height: '100%', transition: 'border-color 0.15s'
                }}>
                <div style={{
                    display: 'inline-block', fontSize: '9px', fontFamily: 'sans-serif',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    padding: '2px 7px', borderRadius: '2px',
                    background: tagColor || '#1e2a3a', color: '#e8dfc8', width: 'fit-content'
                }}>
                    {tag}
                </div>
                <div style={{
                    fontSize: '14px', fontWeight: 600, color: '#1e2a3a',
                    lineHeight: 1.2, fontFamily: "'EB Garamond', Georgia, serif"
                }}>
                    {title}
                </div>
                <div style={{
                    fontSize: '11px', fontFamily: 'sans-serif',
                    color: '#5a6070', lineHeight: 1.5, flex: 1
                }}>
                    {desc}
                </div>
                <div style={{
                    fontSize: '10px', fontFamily: 'sans-serif', color: '#8b1a1a',
                    letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: '2px'
                }}>
                    {cta} →
                </div>
            </div>
        </Link>
    );
};

const ArticleRow = ({ date, title, excerpt, slug, author }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <Link to={`/articles/${slug}`} style={{ textDecoration: 'none' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ padding: '10px 0', borderBottom: '0.5px solid #c8beaa', display: 'flex', gap: '14px' }}>
                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', minWidth: '64px', paddingTop: '2px' }}>
                    {date}
                </div>
                <div>
                    {author && (
                        <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, marginBottom: '2px', letterSpacing: '0.04em' }}>
                            {author}
                        </div>
                    )}
                    <div style={{
                        fontSize: '14px', fontWeight: 600, marginBottom: '2px',
                        color: hovered ? '#8b1a1a' : '#1e2a3a',
                        fontFamily: "'EB Garamond', Georgia, serif",
                        transition: 'color 0.15s'
                    }}>
                        {title}
                    </div>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#5a6070', lineHeight: 1.45 }}>
                        {excerpt}
                    </div>
                </div>
            </div>
        </Link>
    );
};

const SectionLabel = ({ eyebrow, title }) => (
    <>
        <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '2px' }}>
            {eyebrow}
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: '#1e2a3a', marginBottom: '3px', fontFamily: "'EB Garamond', Georgia, serif" }}>
            {title}
        </div>
        <div style={{ width: '36px', height: '2px', background: '#8b1a1a', marginBottom: '0.9rem' }} />
    </>
);

const SAMPLE_ARTICLES = [
    { id: 1, title: 'What "no kings" actually means in a constitutional republic', excerpt: 'A precise legal argument rooted in Article II and the 14th Amendment.', slug: 'what-no-kings-means', display_date: 'May 6, 2026' },
    { id: 2, title: 'Anti-corruption is not a partisan issue', excerpt: 'Both parties have exploited the same loopholes. Real accountability holds everyone to the same standard.', slug: 'anti-corruption-not-partisan', display_date: 'Apr 28, 2026' },
    { id: 3, title: 'The consent of the governed: a quick primer', excerpt: 'Where the phrase comes from, what it meant in 1776, and why it matters now.', slug: 'consent-of-the-governed', display_date: 'Apr 15, 2026' }
];

const HomePage = () => {
    const location  = useLocation();
    const width     = useWindowWidth();
    const isMobile  = width < 768;
    const [memberCount, setMemberCount] = useState(null);
    const [articles,    setArticles]    = useState(SAMPLE_ARTICLES);
    const [mostRead,    setMostRead]    = useState([]);
    const [settings,    setSettings]    = useState({});
    const [threads,     setThreads]     = useState([]);

    useEffect(() => {
        const delay = setTimeout(() => {
            api.get('/users/count')
                .then(res => setMemberCount(res.data.count))
                .catch(() => setMemberCount(null));
            api.get('/articles?limit=3&status=published')
                .then(res => setArticles(res.data.articles || SAMPLE_ARTICLES))
                .catch(() => {});
            api.get('/articles/most-read?limit=3&days=30')
                .then(res => setMostRead(res.data.articles || []))
                .catch(() => {});
            api.get('/admin/settings/public')
                .then(res => setSettings(res.data.settings || {}))
                .catch(() => {});
            api.get('/forum/threads?limit=3')
                .then(res => setThreads(res.data.threads || []))
                .catch(() => {});
        }, 500);
        return () => clearTimeout(delay);
    }, []);

    const displayCount = memberCount ? memberCount.toLocaleString() : '...';

    return (
        <>
            <Helmet>
                <title>By Our Consent — Est. 1776 / Reclaimed 2026</title>
                <meta name="description" content="Constitutional governance, civic accountability, and government by the people. Non-partisan. No party affiliation." />
            </Helmet>

            <Nav />

            {/* HERO */}
            <div style={{ background: '#c8b99a', position: 'relative', overflow: 'hidden', height: isMobile ? '150px' : '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '2px solid #a09070' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, #c2b28a 60%, #9a8660 100%)' }} />
                {!isMobile && (
                    <>
                        <div style={{ position: 'absolute', left: '16px', top: '10px', opacity: 0.15, fontFamily: "'EB Garamond', Georgia, serif", fontStyle: 'italic', fontSize: '10px', color: '#1e0e00', lineHeight: 1.9 }}>
                            We the People of the United States<br/>
                            in Order to form a more perfect Union<br/>
                            establish Justice, insure domestic Tranquility<br/>
                            provide for the common defence, promote<br/>
                            the general Welfare, and secure the Blessings<br/>
                            of Liberty to ourselves and our Posterity
                        </div>
                        <div style={{ position: 'absolute', right: '16px', bottom: '10px', opacity: 0.15, fontFamily: "'EB Garamond', Georgia, serif", fontStyle: 'italic', fontSize: '11px', color: '#1e0e00', lineHeight: 2, textAlign: 'right' }}>
                            A. Constitutionstate<br/>Milagnas Wulfund<br/>William J. Goz
                        </div>
                    </>
                )}
                <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '0 1rem' }}>
                    <div style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: isMobile ? '16px' : '24px', color: '#2a1e08', opacity: 0.6, marginBottom: '2px' }}>We the People</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '8px' : '14px' }}>
                        <ShieldLarge />
                        <div style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: isMobile ? '28px' : '46px', fontWeight: 600, color: '#1e2a3a', lineHeight: 1 }}>By Our Consent</div>
                    </div>
                    <div style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: isMobile ? '13px' : '17px', fontStyle: 'italic', color: '#5a3a1a', marginTop: '4px' }}>
                        Est. 1776 &nbsp;/&nbsp; Reclaimed 2026
                    </div>
                </div>
            </div>

            {/* MEMBER BAR */}
            <div style={{ background: '#ede8dc', padding: '0.7rem 1.5rem', borderBottom: '0.5px solid #c8beaa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Link to="/register" state={{ backgroundLocation: location }} style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '7px 16px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px', whiteSpace: 'nowrap' }}>
                            Join the movement
                        </Link>
                    </div>
                    {/* Premium CTA */}
                    {!isMobile ? (
                        <Link to="/premium" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', background: 'linear-gradient(135deg, #1e2a3a, #2e3d52)', color: '#e8dfc8', padding: '11px 24px', fontFamily: 'sans-serif', textDecoration: 'none', borderRadius: '4px', whiteSpace: 'nowrap', border: '0.5px solid #8b6a3a', boxShadow: '0 2px 6px rgba(30, 42, 58, 0.25)' }}>
                            <span style={{ fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>⭐ Premium Membership</span>
                            <span style={{ fontSize: '10px', letterSpacing: '0.02em', textTransform: 'none', color: '#c9bfa0', fontStyle: 'italic' }}>Specialized tools for research &amp; document creation</span>
                        </Link>
                    ) : (
                        <Link to="/premium" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', background: 'linear-gradient(135deg, #1e2a3a, #2e3d52)', color: '#e8dfc8', padding: '9px 18px', fontFamily: 'sans-serif', textDecoration: 'none', borderRadius: '4px', border: '0.5px solid #8b6a3a', boxShadow: '0 2px 6px rgba(30, 42, 58, 0.25)' }}>
                            <span style={{ fontSize: '12px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>⭐ Premium Membership</span>
                            <span style={{ fontSize: '9px', textTransform: 'none', color: '#c9bfa0', fontStyle: 'italic' }}>Research &amp; document tools</span>
                        </Link>
                    )}
                </div>
                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', fontStyle: 'italic', marginTop: '4px' }}>
                    {settings.site_tagline || 'Government of the people. Subject to the people. Answerable to the law.'}
                </div>
            </div>

            {/* PRINCIPLES */}
            <div style={{ padding: isMobile ? '1rem' : '1.25rem 1.5rem', background: '#f5f0e8' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <SectionLabel eyebrow="What we stand for" title={settings.homepage_hero_text || 'No kings. No oligarchs. One law.'} />
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
                        <PrincipleCard
                            icon={<svg width="22" height="22" viewBox="0 0 32 32" fill="none"><rect x="4" y="6" width="24" height="20" rx="2" stroke="#1e2a3a" strokeWidth="1.2" fill="none"/><line x1="10" y1="13" x2="22" y2="13" stroke="#8b1a1a" strokeWidth="1.2"/><line x1="10" y1="17" x2="18" y2="17" stroke="#8b1a1a" strokeWidth="1.2"/><line x1="10" y1="21" x2="20" y2="21" stroke="#8b1a1a" strokeWidth="1.2"/></svg>}
                            title="Constitutional governance"
                            body="The Constitution is not a suggestion. Every law, every action, every official is subject to it."
                        />
                        <PrincipleCard
                            icon={<svg width="22" height="22" viewBox="0 0 32 32" fill="none"><path d="M16 4L4 10v8c0 6 5.3 11.3 12 13 6.7-1.7 12-7 12-13v-8L16 4z" stroke="#1e2a3a" strokeWidth="1.2" fill="none"/><polyline points="10,16 14,20 22,12" stroke="#8b1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
                            title="No one is above the law"
                            body="Exposing Corruption. Defending Democracy. Billionaires, politicians, executives — everyone answers to the same legal system."
                        />
                        <PrincipleCard
                            icon={<svg width="22" height="22" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="12" r="5" stroke="#1e2a3a" strokeWidth="1.2" fill="none"/><path d="M6 26c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="#1e2a3a" strokeWidth="1.2" fill="none"/><circle cx="8" cy="12" r="3" stroke="#8b1a1a" strokeWidth="1" fill="none"/><circle cx="24" cy="12" r="3" stroke="#8b1a1a" strokeWidth="1" fill="none"/></svg>}
                            title="Government by the people"
                            body="Elected officials serve at our consent, earned through accountability and representation."
                        />
                    </div>
                </div>
            </div>

            {/* FEATURE HUB */}
            <div style={{ padding: isMobile ? '1rem' : '1.5rem 1.5rem', background: '#ede8dc', borderTop: '0.5px solid #c8beaa', borderBottom: '0.5px solid #c8beaa' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <SectionLabel eyebrow="What's happening now" title="Stay informed. Stay engaged." />
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
                        <HubCard tag={settings.hub1_tag || "Live today"}    tagColor="#8b1a1a" title={settings.hub1_title || "Today's constitutional brief"}   desc={settings.hub1_desc || "Senate Judiciary votes on oversight bill. Supreme Court issues ruling on executive privilege. Daily audio brief available."} cta={settings.hub1_cta || "Read + listen"} to="/articles" />
                        <HubCard tag={settings.hub2_tag || "Weekly digest"} tagColor="#1e2a3a" title={settings.hub2_title || "This week in the constitution"}  desc={settings.hub2_desc || "Seven developments this week every citizen should know about. Curated, non-partisan, factual."} cta={settings.hub2_cta || "Read the digest"} to="/articles" />
                        <HubCard tag={settings.hub3_tag || "New drop"}      tagColor="#5a3a1a" title={settings.hub3_title || "Power of the people — new tee"} desc={settings.hub3_desc || "Limited run. Constitution print on premium cotton. Drops Friday — get notified before it sells out."} cta={settings.hub3_cta || "Shop the movement"} to="/store" />
                    </div>
                </div>
            </div>

            {/* MERCH STRIP */}
            <div style={{ background: '#1e2a3a', borderTop: '2px solid #8b1a1a', borderBottom: '2px solid #8b1a1a', padding: isMobile ? '1.25rem 1rem' : '1.25rem 1.5rem' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '24px' }}>

                    {/* Left shirt */}
                    <Link to="/store" style={{ display: 'block', textDecoration: 'none', flexShrink: 0 }}>
                        <img
                            src="/images/shirt-black.jpg"
                            alt="We the People tee — black"
                            style={{ width: isMobile ? '80px' : '120px', height: isMobile ? '80px' : '120px', objectFit: 'cover', objectPosition: 'top', borderRadius: '3px', border: '0.5px solid #3a4e64', display: 'block' }}
                        />
                    </Link>

                    {/* Center text */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '4px' }}>
                            Wear the movement
                        </div>
                        <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.2, marginBottom: '10px' }}>
                            Show where you stand
                        </div>
                        <Link to="/store" style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '7px 18px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                            Shop the collection →
                        </Link>
                    </div>

                    {/* Right shirt */}
                    <Link to="/store" style={{ display: 'block', textDecoration: 'none', flexShrink: 0 }}>
                        <img
                            src="/images/shirt-pebble.jpg"
                            alt="Government for the people tee — pebble"
                            style={{ width: isMobile ? '80px' : '120px', height: isMobile ? '80px' : '120px', objectFit: 'cover', objectPosition: 'top', borderRadius: '3px', border: '0.5px solid #3a4e64', display: 'block' }}
                        />
                    </Link>

                </div>
            </div>

            {/* ARTICLES */}
            <div style={{ padding: isMobile ? '1rem' : '1.5rem 1.5rem', background: '#f5f0e8' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <SectionLabel eyebrow="From the desk" title="Articles & commentary" />

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 260px', gap: '2rem', alignItems: 'start' }}>

                        {/* Recent articles */}
                        <div>
                            {articles.map((a, i) => (
                                <ArticleRow
                                    key={a.id || i}
                                    date={a.display_date || (a.published_at ? new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '')}
                                    author={a.author_name || null}
                                    title={a.title}
                                    excerpt={a.excerpt}
                                    slug={a.slug}
                                />
                            ))}
                            <div style={{ marginTop: '0.9rem' }}>
                                <Link to="/articles" style={{ background: 'transparent', border: '0.5px solid #8a8070', padding: '6px 18px', fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', borderRadius: '3px', letterSpacing: '0.06em', textDecoration: 'none', display: 'inline-block' }}>
                                    View all articles
                                </Link>
                            </div>
                        </div>

                        {/* Most read sidebar */}
                        <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1rem' }}>
                            <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px' }}>
                                Most read
                            </div>
                            {mostRead.length === 0 ? (
                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    Check back soon
                                </div>
                            ) : (
                                mostRead.map((a, i) => (
                                    <Link key={a.id} to={`/articles/${a.slug}`} style={{ textDecoration: 'none', display: 'block', marginBottom: '12px', paddingBottom: '12px', borderBottom: i < mostRead.length - 1 ? '0.5px solid #c8beaa' : 'none' }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                            <span style={{ fontSize: '18px', fontWeight: 700, color: '#c8beaa', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1, flexShrink: 0, width: '20px' }}>
                                                {i + 1}
                                            </span>
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.3, marginBottom: '3px' }}>
                                                    {a.title}
                                                </div>
                                                <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                    {a.unique_view_count > 0
                                                        ? `${a.unique_view_count.toLocaleString()} readers`
                                                        : a.category_name || ''}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* DISCUSSION THREADS */}
            <div style={{ padding: isMobile ? '1rem' : '1.5rem 1.5rem', background: '#ede8dc', borderTop: '0.5px solid #c8beaa' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <SectionLabel eyebrow="Community" title="Latest discussions" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '1rem' }}>
                        {threads.length === 0 ? (
                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070', padding: '1rem', background: '#f5f0e8', borderRadius: '3px', border: '0.5px solid #c8beaa' }}>
                                No discussions yet — check back soon.
                            </div>
                        ) : (
                            threads.map(t => (
                                <Link key={t.id} to={`/forum/${t.slug}`} style={{ textDecoration: 'none' }}>
                                    <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b1a1a', borderRadius: '3px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                        <div>
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '2px' }}>
                                                {t.title}
                                            </div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                {t.reply_count || 0} replies · {t.author_first} {t.author_last}
                                                {t.category_name && ` · ${t.category_name}`}
                                            </div>
                                        </div>
                                        <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b1a1a', flexShrink: 0 }}>Join discussion →</span>
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>
                    <Link to="/forum" style={{ background: 'transparent', border: '0.5px solid #8a8070', padding: '6px 18px', fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', borderRadius: '3px', letterSpacing: '0.06em', textDecoration: 'none', display: 'inline-block' }}>
                        View all discussions
                    </Link>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default HomePage;
